import { Center, Loader, Radio, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Content, NoContentPlaceholder } from '@/core/components';

import { formatDateTime } from '@feature/utils';

import { TravelReferenceDocumentViewer } from './components/TravelReferenceDocumentViewer';
import { getTravelReferenceDocumentsOptions } from './model/TravelReferenceDocumentQuery';
import {
  TravelReferenceDocumentFile,
  TravelReferenceDocumentKind,
  TravelReferenceDocumentSlot,
} from './model/TravelReferenceDocumentTypes';

interface TravelReferenceDocumentBrowseProps {
  kind: TravelReferenceDocumentKind | undefined;
  onKindChange: (kind: TravelReferenceDocumentKind) => void;
}

const isPresent = (slot: TravelReferenceDocumentSlot): slot is TravelReferenceDocumentFile => slot.present;

export function TravelReferenceDocumentBrowse({ kind, onKindChange }: TravelReferenceDocumentBrowseProps) {
  const { t } = useTranslation();
  const { data, isError, isPending } = useQuery(getTravelReferenceDocumentsOptions());
  const documents = (data ?? []).filter(isPresent);
  const selected =
    documents.find(document => document.kind === kind) ??
    documents.find(document => document.kind === 'USER_GUIDE') ??
    documents[0];

  const renderBody = () => {
    if (isError && data == null) {
      return <Text c="red">{t('travel.referenceDocuments.listFailed')}</Text>;
    }
    if (isPending) {
      return (
        <Center h={200}>
          <Loader
            role="status"
            aria-label={t('travel.referenceDocuments.loading')}
          />
        </Center>
      );
    }
    const listFailed = isError && <Text c="red">{t('travel.referenceDocuments.listFailed')}</Text>;
    if (selected == null) {
      return (
        <>
          {listFailed}
          <NoContentPlaceholder title={t('travel.referenceDocuments.empty')} />
        </>
      );
    }
    return (
      <>
        {listFailed}
        <Radio.Group
          aria-label={t('travel.referenceDocuments.title')}
          value={selected.kind}
          onChange={value => onKindChange(value as TravelReferenceDocumentKind)}
        >
          <Stack gap="sm">
            {documents.map(document => (
              <Radio
                key={document.kind}
                value={document.kind}
                label={t(`travel.referenceDocuments.kindEnum.${document.kind}`)}
              />
            ))}
          </Stack>
        </Radio.Group>
        <Text
          size="sm"
          c="dimmed"
        >
          {t(
            selected.uploadedByName == null
              ? 'travel.referenceDocuments.uploadedNoName'
              : 'travel.referenceDocuments.uploaded',
            { date: formatDateTime(selected.uploadedAt), name: selected.uploadedByName },
          )}
        </Text>
        {selected.role != null && (
          <Text
            size="sm"
            c="dimmed"
          >
            {t('travel.referenceDocuments.guideRoleNote', { role: t(`roleType.${selected.role}`) })}
          </Text>
        )}
        <TravelReferenceDocumentViewer
          key={`${selected.kind}:${selected.role ?? ''}:${selected.uploadedAt}`}
          document={selected}
        />
      </>
    );
  };

  return (
    <Content
      bg="white"
      title={t('travel.referenceDocuments.title')}
    >
      {renderBody()}
    </Content>
  );
}
